import React from 'react';
import { Flex, Box, Text, Button, Icons } from '@shared/ui';
import { replacePlaceholder } from 'libs/replacePlaceholder';
import dayjs from 'dayjs';
import i18n from '@shared/i18n';

interface CouponItemProps {
    coupon: BuyerPromoCoupon;
    isSelected: boolean;
    isCartCalculating: boolean | undefined;
    selectedCouponId: string | null;
    couponSavingPrice: Price | undefined;
    handleSelectedCouponOnChange: (id: string) => void;
}

export const CouponItem: React.FC<CouponItemProps> = ({
    coupon,
    isSelected,
    isCartCalculating,
    selectedCouponId,
    couponSavingPrice,
    handleSelectedCouponOnChange,
}) => {
    const border = isSelected ? 'solid 2px' : 'solid 1px';
    const borderColor = isSelected ? 'secondary.040' : 'neutral.020';

    const getBgColor = () => {
        if (!coupon.is_qualified) return 'neutral.020';

        return isSelected ? 'secondary.010' : 'primary.010';
    };

    const bgColor = getBgColor();

    const hasCouponSavingPrice =
        couponSavingPrice?.amount && Number(couponSavingPrice.amount) > 0;

    return (
        <Flex
            minH="93px"
            w="100%"
            overflow="hidden"
            pointerEvents={coupon.is_qualified ? 'auto' : 'none'}
            onClick={() => handleSelectedCouponOnChange(coupon.id)}
            borderRadius="lg"
            cursor="pointer"
            position="relative"
            {...(isSelected && {
                _after: {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: 0,
                    height: 0,
                    border: 'solid',
                    borderWidth: '0 32px 32px 0',
                    borderColor: 'transparent #F16C5D transparent transparent',
                },
            })}
        >
            {isSelected && (
                <Box
                    position="absolute"
                    top="3px"
                    right="3px"
                    color="neutral.000"
                    zIndex="1"
                >
                    <Icons.csr.Check size="12px" weight="bold" />
                </Box>
            )}
            <Flex
                borderRadius="lg"
                border={border}
                borderRight="dashed 1px"
                borderColor={borderColor}
                flexDirection="column"
                gap="xs"
                p="s"
                flex="1"
            >
                <Flex
                    gap="xs"
                    flexDirection="column"
                    opacity={coupon.is_qualified ? 1 : 0.5}
                >
                    <Text fontWeight="bold" color="neutral.120">
                        {coupon.name}
                    </Text>
                    <Flex gap="11px" alignItems="center">
                        <Box color="neutral.070">
                            <Icons.csr.Ticket weight="bold" size="16px" />
                        </Box>
                        {replacePlaceholder(
                            coupon.promotional_copy.text,
                            coupon.promotional_copy.placeholder
                        )}
                    </Flex>
                    <Flex gap="11px" alignItems="center">
                        <Box color="neutral.070">
                            <Icons.csr.CalendarBlank weight="bold" size="16px" />
                        </Box>
                        <Text>
                            {i18n.gettext('使用期限：')}
                            {coupon.end_datetime
                                ? dayjs
                                      .unix(coupon.end_datetime)
                                      .format('YYYY/MM/DD hh:mm')
                                : i18n.gettext('不限')}
                        </Text>
                    </Flex>
                </Flex>
                {coupon.shortfall && (
                    <Flex gap="11px" alignItems="center">
                        <Box color="warning.060">
                            <Icons.csr.Warning size="16px" weight="fill" />
                        </Box>
                        {replacePlaceholder(
                            coupon.shortfall.text,
                            coupon.shortfall.placeholder
                        )}
                    </Flex>
                )}
            </Flex>
            <Flex
                borderRadius="lg"
                border={border}
                borderLeft="dashed 1px"
                borderColor={borderColor}
                alignItems="center"
                justifyContent="center"
                bg={bgColor}
                w="63px"
                position="relative"
            >
                {hasCouponSavingPrice && selectedCouponId === coupon.id ? (
                    <Flex flexDirection="column" alignItems="center">
                        <Text color="secondary.040">{i18n.gettext('優惠')}</Text>
                        <Text color="secondary.040" fontWeight="bold" textAlign="center">
                            {couponSavingPrice.value}
                        </Text>
                    </Flex>
                ) : (
                    <Button
                        isDisabled={!coupon.is_qualified}
                        isLoading={selectedCouponId === coupon.id && isCartCalculating}
                        variant="outline"
                        border="none"
                        boxShadow="none"
                        _hover={{
                            bg: 'none',
                        }}
                        color={coupon.is_qualified ? 'primary.040' : 'neutral.060'}
                    >
                        {i18n.gettext('選取')}
                    </Button>
                )}
                <Box
                    background="neutral.000"
                    borderRadius="full"
                    position="absolute"
                    border={border}
                    borderColor={borderColor}
                    top="0"
                    left="-8px"
                    transform="translateY(-50%)"
                    h="14px"
                    w="14px"
                />
                <Box
                    background="neutral.000"
                    borderRadius="full"
                    position="absolute"
                    border={border}
                    borderColor={borderColor}
                    bottom="0"
                    left="-8px"
                    transform="translateY(50%)"
                    h="14px"
                    w="14px"
                />
            </Flex>
        </Flex>
    );
};
